import { Search, X, Moon, Sun, Menu, CircleX } from 'lucide-react';
import { ImageUploadOutboxIndicator } from '@/components/ImageUploadOutboxIndicator';
import NotificationBell from '@/components/NotificationBell';
import { useTranslation } from 'react-i18next';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogTrigger,
  DialogTitle,
} from '@/components/ui/dialog';
import { useAuthLite } from '@/context/AuthLite';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useState, useRef, useEffect, useCallback } from 'react';
import { useGlobalSearch } from '@/hooks/useGlobalSearch';
import GlobalSearchPanel from './GlobalSearchPanel';
import { useTheme } from './ThemeProvider';
import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';
import type { SearchScope } from '@/lib/search-utils';

interface HeaderProps {
  onMenuClick?: () => void;
}

export default function Header({ onMenuClick }: HeaderProps = {}) {
  const { user, logout, authMode } = useAuthLite();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation();
  const { theme, setTheme, resolvedTheme } = useTheme();
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [searchScope, setSearchScope] = useState<SearchScope>('all');
  const searchInputRef = useRef<HTMLInputElement>(null);
  const isMobile = useIsMobile();

  // Detect platform for keyboard shortcut display
  const isMac = typeof window !== 'undefined' && (navigator.platform.toUpperCase().indexOf('MAC') >= 0 || navigator.userAgent.toUpperCase().indexOf('MAC') >= 0);

  // Debounce search query to avoid too many API calls
  // Short delay for snappier feel while still preventing excessive API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, 150); // Wait 150ms after user stops typing

    return () => clearTimeout(timer);
  }, [searchQuery]);

  const { results, isLoading } = useGlobalSearch(debouncedQuery, searchScope);

  const searchWasOpenRef = useRef(false);
  /** Keresőből deal/kontakt megnyitásakor ne fusson setSearchParams(sq törlés) — visszaváltotta az URL-t. */
  const skipSqCleanupOnCloseRef = useRef(false);

  const leaveSearch = useCallback(
    (path: string) => {
      skipSqCleanupOnCloseRef.current = true;
      navigate(path, { flushSync: true, replace: true });
      setSearchOpen(false);
    },
    [navigate],
  );

  useEffect(() => {
    if (searchOpen) {
      searchWasOpenRef.current = true;
      const sq = searchParams.get('sq');
      if (sq) {
        setSearchQuery((prev) => (prev.trim() === '' ? sq : prev));
      }
      return;
    }
    if (!searchWasOpenRef.current) return;
    searchWasOpenRef.current = false;
    if (skipSqCleanupOnCloseRef.current) {
      skipSqCleanupOnCloseRef.current = false;
      return;
    }
    if (!searchParams.get('sq')) return;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('sq');
        return next;
      },
      { replace: true },
    );
  }, [searchOpen, searchParams, setSearchParams]);

  useEffect(() => {
    if (!searchOpen) return;
    const path = window.location.pathname;
    if (path.startsWith('/crm/deals/') || path.startsWith('/contacts/')) return;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (debouncedQuery.trim()) next.set('sq', debouncedQuery.trim());
        else next.delete('sq');
        return next;
      },
      { replace: true },
    );
  }, [debouncedQuery, searchOpen, setSearchParams]);

  // Keyboard shortcuts: Ctrl+K / Cmd+K to open search; Esc / overlay bezárja a dialógust (nincs külön Zavrieť gomb desktopon).
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen(true);
        setTimeout(() => {
          searchInputRef.current?.focus();
        }, 100);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const userInitials = user?.name
    ?.split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase() || 'U';

  return (
    <header className={cn(
      "sticky top-0 z-40 w-full bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b border-border shadow-sm",
      "max-[767px]:pt-[env(safe-area-inset-top)] max-[767px]:min-h-[calc(4rem+env(safe-area-inset-top))]"
    )}>
      <div className={cn(
        "flex items-center justify-between px-3 md:px-6 gap-3 md:gap-6 h-14",
        isMobile && "!px-3 !gap-3"
      )}>
        {/* Left: Mobile Menu Button */}
        {isMobile && onMenuClick && (
          <Button
            variant="ghost"
            size="icon"
            onClick={onMenuClick}
            className="md:hidden"
            aria-label={t('header.openMenu')}
          >
            <Menu className="h-5 w-5" />
          </Button>
        )}

        {/* Search */}
        <Dialog open={searchOpen} onOpenChange={setSearchOpen}>
          <DialogTrigger asChild>
            <div className={`flex-1 ${isMobile ? 'max-w-none' : 'max-w-md'}`} onClick={() => setSearchOpen(true)}>
              <div className="relative">
                <Search className={`absolute ${isMobile ? 'left-2' : 'left-3'} top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400`} />
                <Input
                  type="text"
                  placeholder={t('header.searchPlaceholder')}
                  className={`w-full ${isMobile ? 'pl-8 pr-8' : 'pl-10 pr-20'} py-2 bg-muted border-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none rounded-lg focus:bg-background transition-colors duration-200 cursor-pointer shadow-none text-sm`}
                  readOnly
                />
                {!isMobile && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1 text-xs text-muted-foreground pointer-events-none">
                    {isMac ? (
                      <>
                        <kbd className="px-1.5 py-0.5 rounded border border-border bg-muted/50 font-mono text-[10px]">⌘</kbd>
                        <kbd className="px-1.5 py-0.5 rounded border border-border bg-muted/50 font-mono text-[10px]">K</kbd>
                      </>
                    ) : (
                      <>
                        <kbd className="px-1.5 py-0.5 rounded border border-border bg-muted/50 font-mono text-[10px]">Ctrl</kbd>
                        <kbd className="px-1.5 py-0.5 rounded border border-border bg-muted/50 font-mono text-[10px]">K</kbd>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          </DialogTrigger>

          <DialogContent
            className={cn(
              "p-0",
              isMobile
                ? "overflow-hidden fixed inset-0 top-0 left-0 right-0 bottom-0 w-full max-w-none h-full max-h-[100dvh] rounded-none translate-x-0 translate-y-0 data-[state=open]:slide-in-from-none data-[state=closed]:slide-out-to-none pt-[max(calc(env(safe-area-inset-top)+0.5rem),2rem)] pl-[max(0.75rem,env(safe-area-inset-left))] pr-[max(0.75rem,env(safe-area-inset-right))] box-border"
                : "overflow-visible max-w-6xl h-[85vh] w-[min(100vw-2rem,72rem)] sm:max-w-6xl sm:rounded-lg"
            )}
            showClose={false}
            onOpenAutoFocus={(e) => isMobile && e.preventDefault()}
          >
            <DialogTitle className="sr-only">{t('header.searchDialogTitle')}</DialogTitle>
            <div
              className={cn(
                "h-full max-h-[100dvh] flex flex-col min-h-0",
                isMobile ? "overflow-hidden" : "overflow-visible",
              )}
            >
              {/* Search Header – kompakt mobilon, safe area és szélesség kezelés */}
              <div className={cn("flex-shrink-0 border-b border-border bg-background", isMobile && "pt-10")}>
                <div
                  className={cn(
                    "flex min-w-0 items-stretch gap-2",
                    isMobile ? "px-3 py-2" : "px-4 py-3 sm:px-6 sm:py-4",
                  )}
                >
                  {/* Egy keret: nincs „doboz a dobozban”; törlés ≠ bezárás ikon */}
                  <div className="flex min-h-10 min-w-0 flex-1 items-stretch gap-2 rounded-xl border border-border bg-muted/20 px-2.5 py-1 sm:min-h-11 sm:px-3 sm:py-1.5">
                    <Search
                      className={cn(
                        "pointer-events-none shrink-0 self-center text-muted-foreground",
                        isMobile ? "h-4 w-4" : "h-5 w-5",
                      )}
                      aria-hidden
                    />
                    <Input
                      id="global-search-input"
                      ref={searchInputRef}
                      type="text"
                      placeholder={t('header.searchPlaceholderLong')}
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      autoFocus={!isMobile}
                      className={cn(
                        "min-h-0 flex-1 border-0 bg-transparent shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none",
                        isMobile ? "h-8 py-1 text-sm" : "h-10 py-2 text-base",
                      )}
                    />
                    {searchQuery ? (
                      <button
                        type="button"
                        onClick={() => setSearchQuery('')}
                        className="shrink-0 self-center rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                        aria-label={t('search.clearQuery')}
                      >
                        <CircleX className={isMobile ? "h-4 w-4" : "h-5 w-5"} />
                      </button>
                    ) : null}
                  </div>
                  {isMobile && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-10 shrink-0 px-2.5 text-sm font-medium text-primary"
                      onClick={() => setSearchOpen(false)}
                    >
                      {t('common.cancel')}
                    </Button>
                  )}
                </div>
                {/* Scope chips – mobilon vízszintes görgetés, asztalon wrap */}
                <div
                  className={cn(
                    isMobile ? "overflow-x-auto scrollbar-hide px-3 pb-1.5" : "px-4 pb-3 pt-1 sm:px-6 sm:pt-1.5",
                  )}
                >
                  <div className={cn("flex gap-1.5", isMobile ? "min-w-max pr-4" : "flex-wrap gap-2")}>
                    {(['all', 'deals', 'contacts', 'notes'] as const).map((scope) => (
                      <button
                        key={scope}
                        type="button"
                        onClick={() => setSearchScope(scope)}
                        className={cn(
                          "shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap",
                          searchScope === scope
                            ? "bg-primary text-primary-foreground"
                            : "bg-muted text-muted-foreground hover:bg-muted/80",
                        )}
                      >
                        {scope === 'all' && t('search.scopeAll')}
                        {scope === 'deals' && t('search.scopeDeals')}
                        {scope === 'contacts' && t('search.scopeContacts')}
                        {scope === 'notes' && t('search.scopeNotes')}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Search Results – mobilon extra pt a keresősor magasságával (~placeholder szöveg) */}
              <div
                className={cn(
                  "flex-1 min-h-0",
                  isMobile ? "overflow-hidden pt-9" : "overflow-visible",
                )}
              >
                <GlobalSearchPanel
                  results={results}
                  query={debouncedQuery}
                  isLoading={isLoading}
                  leaveSearch={leaveSearch}
                />
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Right: Actions & Avatar */}
        <div className="flex items-center gap-2 md:gap-3">
          {user ? <ImageUploadOutboxIndicator /> : null}
          {user && !isMobile ? (
            <NotificationBell variant="header" />
          ) : (
            <button
              onClick={() => setTheme(resolvedTheme === 'dark' ? 'light' : 'dark')}
              className="p-2 rounded-lg hover:bg-accent transition-colors duration-200"
              aria-label={t('header.toggleTheme')}
            >
              {resolvedTheme === 'dark' ? (
                <Sun className="w-5 h-5 text-foreground" />
              ) : (
                <Moon className="w-5 h-5 text-foreground" />
              )}
            </button>
          )}
          {/* Avatar Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="rounded-full p-0 w-9 h-9 md:w-10 md:h-10">
                <Avatar className="w-9 h-9 md:w-10 md:h-10">
                  {user?.picture && (
                    <AvatarImage src={user.picture} alt={user.name || t('header.profile')} className="object-cover" />
                  )}
                  <AvatarFallback className="bg-primary text-primary-foreground font-semibold text-xs md:text-sm">
                    {userInitials}
                  </AvatarFallback>
                </Avatar>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem disabled>
                <div className="flex flex-col">
                  <span className="font-medium">{user?.name}</span>
                  <span className="text-xs text-muted-foreground">{user?.email}</span>
                </div>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem>
                <a href="/settings" className="w-full">
                  {t('header.profile')}
                </a>
              </DropdownMenuItem>
              <DropdownMenuItem>
                <a href="/settings" className="w-full">
                  {t('header.settings')}
                </a>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              {authMode === "email" && (
                <DropdownMenuItem
                  onClick={async () => {
                    await logout();
                    navigate("/login", { replace: true });
                  }}
                >
                  {t('header.logout')}
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
}
